"use client";

import { ChevronDownIcon } from "lucide-react";
import { type ReactNode, useEffect, useRef } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { useCollapseContext } from "./collapse-context";

export type CollapsibleCardProps = {
  /** Unique identifier for this card */
  cardId: string;
  /** Header content that is always visible */
  headerContent: ReactNode;
  /** Body content that collapses */
  children: ReactNode;
  /** Additional class name for the outer container */
  className?: string;
  /** Default expanded state (defaults to true) */
  defaultExpanded?: boolean;
};

/**
 * A collapsible card wrapper that integrates with the CollapseContext.
 * The header is always visible with a chevron toggle.
 * The body content collapses/expands with animation.
 */
export function CollapsibleCard({
  cardId,
  headerContent,
  children,
  className,
  defaultExpanded = true,
}: CollapsibleCardProps) {
  const context = useCollapseContext();

  // Use refs to avoid re-running effect when context object reference changes
  const contextRef = useRef(context);
  contextRef.current = context;

  // Register this card with the context on mount
  useEffect(() => {
    const ctx = contextRef.current;
    if (ctx) {
      ctx.registerCard(cardId);
      return () => ctx.unregisterCard(cardId);
    }
  }, [cardId]);

  // If no context, the card is not collapsible (fallback behavior)
  if (!context) {
    return (
      <div className={cn("overflow-hidden rounded-xl border", className)}>
        {headerContent}
        {children}
      </div>
    );
  }

  const isCollapsed = context.isCollapsed(cardId);
  const isOpen = defaultExpanded ? !isCollapsed : isCollapsed;

  return (
    <Collapsible
      className={cn("overflow-hidden rounded-xl border", className)}
      onOpenChange={() => context.toggleCard(cardId)}
      open={isOpen}
    >
      <CollapsibleTrigger asChild>
        <div className="relative cursor-pointer">
          {headerContent}
          <div className="-translate-y-1/2 absolute top-1/2 right-4">
            <ChevronDownIcon
              className={cn(
                "size-5 text-muted-foreground transition-transform duration-200",
                isOpen && "rotate-180"
              )}
            />
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}
